import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon "Beyond The Frame" — 10 İçerik Medya Planı.
 * Aynı kampanya (30 Haz – 16 Ağu 2026, 340K, Trafik, FB+IG) ama içerik bazlı:
 * 2 Story + 4 Post + 4 Carousel = 10 içerik. NET medya içeriklere eşit dağıtılır.
 * Bütçe modeli Beyond Frame ile aynı: AHK %10, Tasarım 35K, Stopaj %15.
 */
export default function CanonBeyondFrameContent() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');

  // ── Bütçe kırılımı (Beyond Frame ile aynı) ─────────────────────────────────
  const TOTAL = 340000;
  const AHK_RATE = 0.10;
  const AHK_FEE = Math.round(TOTAL * AHK_RATE);   // 34.000 — Ajans Hizmet Komisyonu
  const DESIGN = 35000;                            // Tasarım
  const MEDIA_BRUT = TOTAL - AHK_FEE - DESIGN;     // 271.000 (stopaj dahil medya)
  const STOPAJ_RATE = 0.15;
  const STOPAJ = Math.round(MEDIA_BRUT * STOPAJ_RATE); // 40.650
  const NET = MEDIA_BRUT - STOPAJ;                 // 230.350 — Meta'da gerçek harcanan

  const DAYS = 48; // 30 Haziran → 16 Ağustos dahil
  const FREQ = 3.3;
  const dailyNet = NET / DAYS;

  // ── 10 içerik: 2 Story + 4 Post + 4 Carousel ───────────────────────────────
  const FORMAT = {
    story: { label: 'Story', size: '1080×1920', placement: 'IG/FB Stories', cpm: 32, cpc: 3.20 },
    post: { label: 'Post', size: '1080×1350', placement: 'IG/FB Feed', cpm: 30, cpc: 2.80 },
    carousel: { label: 'Carousel', size: '1080×1080', placement: 'IG/FB Feed Carousel', cpm: 28, cpc: 2.60 },
  };
  const PLAN = [
    { type: 'story', n: 2 },
    { type: 'post', n: 4 },
    { type: 'carousel', n: 4 },
  ];
  const COUNT = PLAN.reduce((s, p) => s + p.n, 0); // 10

  // NET'i içeriklere eşit dağıt (son içerik yuvarlama farkını taşır).
  const perContent = Math.round(NET / COUNT);
  let allocated = 0, idx = 0;
  const contents = [];
  PLAN.forEach((p) => {
    const f = FORMAT[p.type];
    for (let i = 1; i <= p.n; i++) {
      idx++;
      const last = idx === COUNT;
      const budget = last ? NET - allocated : perContent;
      allocated += budget;
      contents.push({
        name: `${f.label} ${i}`,
        type: p.type, size: f.size, placement: f.placement, cpm: f.cpm, cpc: f.cpc,
        budget,
        impressions: Math.round((budget / f.cpm) * 1000),
        clicks: Math.round(budget / f.cpc),
        reach: Math.round((budget / f.cpm) * 1000 / FREQ),
      });
    }
  });

  const totals = contents.reduce((t, c) => ({
    budget: t.budget + c.budget, impressions: t.impressions + c.impressions, clicks: t.clicks + c.clicks,
  }), { budget: 0, impressions: 0, clicks: 0 });
  const blendedCpc = totals.budget / totals.clicks;
  const blendedCpm = (totals.budget / totals.impressions) * 1000;
  const estReach = Math.round(totals.impressions / FREQ);
  const ctr = (totals.clicks / totals.impressions) * 100;

  // Format özeti
  const byFormat = PLAN.map((p) => {
    const items = contents.filter((c) => c.type === p.type);
    return { label: FORMAT[p.type].label, n: p.n, budget: items.reduce((s, c) => s + c.budget, 0) };
  });

  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(slideRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 8;
      const w = pw - m * 2;
      const h = (canvas.height * w) / canvas.width;
      let y = m, rem = h;
      pdf.addImage(img, 'JPEG', m, y, w, h);
      rem -= (ph - m * 2);
      while (rem > 0) {
        pdf.addPage();
        y = m - (h - rem);
        pdf.addImage(img, 'JPEG', m, y, w, h);
        rem -= (ph - m * 2);
      }
      pdf.save('Canon_Beyond_The_Frame_10_Icerik_Plani.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  const typeTone = {
    story: 'bg-amber-50 text-amber-700 border-amber-200',
    post: 'bg-blue-50 text-blue-700 border-blue-200',
    carousel: 'bg-violet-50 text-violet-700 border-violet-200',
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex justify-end mb-3">
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: '#CC0000' }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">Beyond The Frame — 10 İçerik Medya Planı</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Trafik · 30 Haziran – 16 Ağustos 2026 · 2 Story + 4 Post + 4 Carousel</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Kampanya detayı */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon'un <strong>“Beyond The Frame”</strong> video yarışması duyurusu. 30 Haziran – 16 Ağustos 2026
              ({DAYS} gün) yayında. Amaç: amatör video içerik üreticilerini Canon ailesine katmak, yarışmaya katılım için
              link'e <strong>trafik</strong> çekmek. <strong>{COUNT} içerik</strong> (2 Story + 4 Post + 4 Carousel) ile
              yürütülür; NET medya bütçesi içeriklere eşit dağıtılır (≈ {fmtCur(perContent)}/içerik).
            </p>
          </div>

          {/* Hedefleme */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Hedef Kitle" value="Videographer" sub="amatör video içerik üreticileri" tone="red" />
            <Stat label="Yaş" value="18–55" />
            <Stat label="Lokasyon" value="Tüm Türkiye" />
            <Stat label="Platform" value="FB + IG" sub="Meta Ads · Trafik" />
          </div>

          {/* Bütçe kırılımı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Bütçe Kırılımı</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Toplam Bütçe" value={fmtCur(TOTAL)} sub="müşteri toplam" />
              <Stat label="AHK (Ajans %10)" value={'-' + fmtCur(AHK_FEE)} sub={`${fmtCur(TOTAL)} × 0.10`} tone="indigo" />
              <Stat label="Tasarım" value={'-' + fmtCur(DESIGN)} sub="içerik üretimi" tone="indigo" />
              <Stat label="Stopaj (%15)" value={'-' + fmtCur(STOPAJ)} sub={`${fmtCur(MEDIA_BRUT)} × 0.15`} tone="indigo" />
              <Stat label="NET Medya" value={fmtCur(NET)} sub="Meta'da harcanan" tone="green" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Günlük ortalama net harcama ≈ {fmtCur(dailyNet)} ({DAYS} gün). AHK + Stopaj + Tasarım toplam 340K'ya dahildir.
              İçerik başına ≈ {fmtCur(perContent)}.
            </p>
          </div>

          {/* Format özeti */}
          <div className="grid grid-cols-3 gap-3">
            {byFormat.map((f) => (
              <div key={f.label} className={`rounded-xl border p-4 ${typeTone[f.label.toLowerCase()]}`}>
                <span className="text-[10px] font-medium uppercase opacity-70">{f.label}</span>
                <p className="text-xl font-bold mt-1">{f.n} içerik</p>
                <p className="text-[10px] opacity-70 mt-0.5">{fmtCur(f.budget)}</p>
              </div>
            ))}
          </div>

          {/* İçerik dağılımı tablosu */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">İçerik & Bütçe Dağılımı ({COUNT} İçerik)</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">İçerik</th>
                    <th className="text-left px-3 py-2 font-semibold">Boyut</th>
                    <th className="text-left px-3 py-2 font-semibold">Yerleşim</th>
                    <th className="text-right px-3 py-2 font-semibold">Bütçe (NET)</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Erişim*</th>
                    <th className="text-right px-3 py-2 font-semibold">Tıklama</th>
                    <th className="text-right px-3 py-2 font-semibold">CPC</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {contents.map((c) => (
                    <tr key={c.name} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-gray-800">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold border mr-1.5 ${typeTone[c.type]}`}>{FORMAT[c.type].label}</span>
                        {c.name}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{c.size}</td>
                      <td className="px-3 py-2 text-gray-500">{c.placement}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtCur(c.budget)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.reach)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c.clicks)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{c.cpc.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{c.cpm.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={3}>TOPLAM ({COUNT} içerik)</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.budget)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.impressions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(estReach)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.clicks)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpc.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpm.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              *Erişim, ~{FREQ} ortalama frekans varsayımıyla tahminidir. Tahmini CTR ≈ %{ctr.toFixed(2)}.
              Sayılar Meta açık artırma dinamiklerine göre değişebilir; optimizasyonla yön verilir.
            </p>
          </div>

          {/* Projeksiyon özeti */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Toplam Tıklama" value={fmtInt(totals.clicks)} sub="link tıklaması (tahmini)" tone="green" />
            <Stat label="Ort. CPC" value={'₺' + blendedCpc.toFixed(2)} />
            <Stat label="Toplam Gösterim" value={fmtInt(totals.impressions)} />
            <Stat label="Tahmini Erişim" value={fmtInt(estReach)} sub={`~${FREQ} frekans`} />
          </div>

          {/* Zamanlama */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-bold text-gray-800">Zamanlama</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Yayın: <strong>30 Haziran – 16 Ağustos 2026</strong> ({DAYS} gün), yarışma süresi boyunca kesintisiz.
              {COUNT} içerik döneme yayılır; duyuru içerikleri başta yoğun, son haftalarda “XX days left / deadline”
              mesajlarıyla aciliyet (urgency) vurgusu. Performansa göre format/yerleşim ağırlıkları optimize edilir.
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-7 w-auto object-contain" />
            <p className="text-[9px] text-gray-400">
              {new Date().toLocaleDateString('tr-TR')} · Confidential · Jellybon × Glowytics
            </p>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-6 w-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}
